import { InfoCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Form,
  FormTitle,
  Input,
  InputNumber,
  Radio,
  Select,
  Tooltip,
  Wrapper,
  message,
  useBreadcrumbRoutes,
} from '@maxtropy/components';
import { Space } from 'antd';
import { ReactNode, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  MONITOR_OBJECT_OPTIONS,
  alarmLevelOptions,
  dispatchPointOptions,
  feedbackPointOptions,
  monitorObjectTypeOptions,
} from '../constants';
import { createMockRule, getMockRule, updateMockRule } from '../mockData';
import { CreateRuleFormValues, MonitorObjectType } from '../types';
import styles from './index.module.scss';

const infoLabel = (label: string, tip: ReactNode) => (
  <span className={styles.infoLabel}>
    {label}
    <Tooltip title={tip}>
      <InfoCircleOutlined className={styles.infoIcon} />
    </Tooltip>
  </span>
);

interface RuleFormPageProps {
  isEdit?: boolean;
}

const RuleFormPage = ({ isEdit = false }: RuleFormPageProps) => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm<CreateRuleFormValues>();
  const monitorObjectType = Form.useWatch('monitorObjectType', form) ?? MonitorObjectType.LOAD;
  const monitorObjectOptions = useMemo(() => MONITOR_OBJECT_OPTIONS[monitorObjectType], [monitorObjectType]);
  const pageTitle = isEdit ? '编辑规则' : '新建规则';

  useEffect(() => {
    if (!isEdit || !id) {
      return;
    }

    const rule = getMockRule(Number(id));
    if (!rule) {
      message.warning('未找到该规则');
      navigate('/dispatch/control/deviation-rule', { replace: true });
      return;
    }

    form.setFieldsValue({
      name: rule.name,
      monitorObjectType: rule.monitorObjectType,
      monitorObjectIds: rule.monitorObjectIds ?? [],
      dispatchPoint: rule.dispatchPoint,
      feedbackPoint: rule.feedbackPoint,
      alarmLevel: rule.alarmLevel,
      monitoringDuration: rule.monitoringDuration,
      deviationRate: rule.deviationRate,
    });
  }, [form, id, isEdit, navigate]);

  const goBack = () => navigate('/dispatch/control/deviation-rule');

  const handleFinish = (values: CreateRuleFormValues) => {
    if (isEdit && id) {
      updateMockRule(Number(id), values);
    } else {
      createMockRule(values);
    }
    message.success('保存成功');
    goBack();
  };

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), { name: pageTitle }]} hasPadding={false}>
      <main className={styles.page}>
        <FormTitle title={pageTitle} className={styles.pageTitle} />
        <Form<CreateRuleFormValues>
          form={form}
          className={styles.form}
          layout="horizontal"
          labelAlign="left"
          labelCol={{ flex: '136px' }}
          wrapperCol={{ flex: 'auto' }}
          colon={false}
          initialValues={{ monitorObjectType: MonitorObjectType.LOAD }}
          onFinish={handleFinish}
        >
          <Form.Item label="规则名称" name="name" rules={[{ required: true, message: '请输入规则名称' }]}>
            <Input maxLength={50} placeholder="请输入" />
          </Form.Item>

          <Form.Item
            label="监听对象类型"
            name="monitorObjectType"
            rules={[{ required: true, message: '请选择监听对象类型' }]}
          >
            <Radio.Group
              options={monitorObjectTypeOptions}
              onChange={() => form.setFieldValue('monitorObjectIds', [])}
            />
          </Form.Item>

          <Form.Item label="监听对象" name="monitorObjectIds" rules={[{ required: true, message: '请选择监听对象' }]}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="请选择"
              options={monitorObjectOptions}
              optionFilterProp="label"
              maxTagCount="responsive"
            />
          </Form.Item>

          <Form.Item
            label={infoLabel('下发调度点位', '下发值来自调度系统实时下发的指令')}
            name="dispatchPoint"
            rules={[{ required: true, message: '请选择下发调度点位' }]}
          >
            <Select allowClear placeholder="请选择" options={dispatchPointOptions} />
          </Form.Item>

          <Form.Item
            label={infoLabel('回显监测点位', '回显值来自设备实际遥测数据')}
            name="feedbackPoint"
            rules={[{ required: true, message: '请选择回显监测点位' }]}
          >
            <Select allowClear placeholder="请选择" options={feedbackPointOptions} />
          </Form.Item>

          <Form.Item label="报警等级" name="alarmLevel" rules={[{ required: true, message: '请选择报警等级' }]}>
            <Select allowClear placeholder="请选择" options={alarmLevelOptions} />
          </Form.Item>

          <Form.Item
            label="监听时间范围"
            name="monitoringDuration"
            rules={[
              { required: true, message: '请输入监听时间范围' },
              { type: 'number', min: 1, max: 99999999, message: '请输入1~99999999的整数' },
            ]}
          >
            <InputNumber min={1} max={99999999} precision={0} placeholder="请输入" addonAfter="s" />
          </Form.Item>

          <Form.Item
            label={infoLabel('偏差率', '偏差率=(回显值-下发值)/下发值×100%')}
            name="deviationRate"
            rules={[
              { required: true, message: '请输入偏差率' },
              { type: 'number', min: 0, max: 100, message: '请输入0~100之间的数值' },
            ]}
          >
            <InputNumber min={0} max={100} precision={2} placeholder="请输入" addonAfter="%" />
          </Form.Item>

          <Space size={8} className={styles.actions}>
            <Button type="primary" htmlType="submit">
              保存
            </Button>
            <Button htmlType="button" onClick={goBack}>
              取消
            </Button>
          </Space>
        </Form>
      </main>
    </Wrapper>
  );
};

export default RuleFormPage;
